import React, { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, WrenchIcon, CalendarIcon, ClockIcon, CurrencyEuroIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';

const HistoryModal = ({ isOpen, onClose, selectedCar }) => {
  const { t } = useTranslation();
  // Store original body styles and scroll position
  const bodyStyles = useRef({});

  // Create a robust scroll lock mechanism
  useEffect(() => {
    if (isOpen) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Store current scroll position and styles
      bodyStyles.current = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        mainOverflow: mainElement?.style.overflow,
      };

      // Apply scroll lock styles
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Also lock the main scrollable element
      if (mainElement) {
        mainElement.style.overflow = 'hidden';
      }
    } else if (bodyStyles.current.scrollY !== undefined) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Restore original styles
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element overflow
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      // Restore scroll position
      if (mainElement) {
        mainElement.scrollTop = bodyStyles.current.scrollY;
      } else {
        window.scrollTo(0, bodyStyles.current.scrollY);
      }
    }

    return () => {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Always restore on unmount
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      if (bodyStyles.current.scrollY !== undefined) {
        if (mainElement) {
          mainElement.scrollTop = bodyStyles.current.scrollY;
        } else {
          window.scrollTo(0, bodyStyles.current.scrollY);
        }
      }
    };
  }, [isOpen]);

  // Map appointments from selectedCar to history format
  const appointmentHistory = useMemo(() => {
    if (!selectedCar || !Array.isArray(selectedCar.appointments)) return [];
    return selectedCar.appointments.map((appt, idx) => {
      // Parse date and time
      const dateObj = new Date(appt.appointmentStart);
      const date = dateObj.toLocaleDateString('ro-RO');
      const time = dateObj.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
      return {
        id: `appt-${idx}`,
        service: appt.specialization || t('history.service'),
        shop: appt.shopName || '-',
        date,
        time: `${time}`,
        cost: appt.cost ? `${appt.cost} RON` : '-',
        status: appt.status === 'PENDING' ? t('history.pending') : (appt.status === 'FINALIZED' ? t('history.finalized') : appt.status)
      };
    });
  }, [selectedCar, t]);

  // Combine appointments and dummy data (appointments first)
  const historyData = useMemo(() => {
    if (appointmentHistory.length > 0) {
      // Optionally, you can concat dummyHistoryData if you want both
      // return [...appointmentHistory, ...dummyHistoryData];
      return appointmentHistory;
    }
    return []; // Return empty if no appointments 
  }, [appointmentHistory]);

  // Function to get service icon color based on service type
  const getServiceColor = (service) => {
    if (service.includes('ulei')) return 'text-amber-500';
    if (service.includes('frână')) return 'text-rose-500';
    if (service.includes('Revizie')) return 'text-blue-500';
    if (service.includes('Diagnostic')) return 'text-purple-500';
    if (service.includes('curea')) return 'text-emerald-500';
    return 'text-slate-500';
  };

  // Calculate total cost (skip if cost is "-")
  const totalCost = useMemo(() => {
    return historyData.reduce((total, item) => {
      if (!item.cost || item.cost === '-') return total;
      const costValue = parseInt(item.cost.split(' ')[0]);
      return total + (isNaN(costValue) ? 0 : costValue);
    }, 0);
  }, [historyData]);

  // Return null if the modal is not open
  if (!isOpen || !selectedCar) return null;

  return (
    <div className="fixed inset-0 z-20 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
      <div className="relative w-full max-w-5xl my-8 mb-20 max-h-svh md:max-h-[calc(80vh-2rem)] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700">
        {/* Header */}
        <div className="p-2  border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
                <WrenchIcon 
                className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
              </div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white truncate">
                  {t('history.title', { plate: selectedCar.plate })}
                </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-2 sm:p-2 overflow-y-auto flex-1 max-h-[calc(80vh-10rem)]">
          {historyData.length > 0 ? (
            <div className="space-y-3">
              {historyData.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-white dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 p-3 sm:p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
                    <div className="flex items-start space-x-3 min-w-0 flex-1">
                      <div className="p-1.5 sm:p-2 bg-purple-500 rounded-full flex-shrink-0">
                        <WrenchIcon className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm sm:text-base font-medium text-slate-900 dark:text-white mb-1 sm:mb-0.5 leading-tight">{item.service}</h4>
                        <div className="flex flex-col sm:flex-row sm:items-center text-xs text-slate-500 dark:text-slate-400 gap-1 sm:gap-2">
                          <span className="flex items-center flex-shrink-0">
                            <BuildingStorefrontIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{item.shop}</span>
                          </span>
                          <span className="hidden sm:inline">•</span>
                          <span className="flex items-center flex-shrink-0">
                            <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                            {item.date} {item.time}
                          </span>
                          <span className="hidden sm:inline">•</span>
                          {/* <span className="flex items-center flex-shrink-0">
                            <ClockIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                            
                          </span> */}
                        </div>
                      </div>
                    </div>
                    {/* <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:gap-1">
                      <span className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                        {item.cost}
                      </span>
                      <span className={`px-2 py-0.5 text-xs leading-4 font-medium rounded-full ${
                        item.status === 'Finalizat' 
                          ? 'bg-emerald-100 text-green-800 dark:bg-green-800/60 dark:text-green-100' 
                          : 'bg-amber-100 text-yellow-800 dark:bg-yellow-800/60 dark:text-yellow-100'
                      }`}>
                        {item.status}
                      </span>
                    </div> */}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 px-4">
              <WrenchIcon className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-slate-400 dark:text-slate-500 mb-4" />
              <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">
                {t('history.noHistory')}
              </p>
            </div>
          )}
        </div>

        {historyData.length > 0 && (
          <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                {t('history.totalServices', { count: historyData.length })}
              </div>
              {/* <div className="flex items-center gap-2 self-end sm:self-auto">
                <span className="text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-200">
                  {t('history.totalCost')}
                </span>
                <span className="text-base sm:text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  {totalCost} RON
                </span>
              </div> */}
            </div>
          </div>
        )}

        <div className="px-3 sm:px-6 py-3 sm:py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end rounded-b-xl flex-shrink-0">
          <button
            type="button"
            className="w-full sm:w-auto bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium py-2 px-4 rounded-lg transition-colors"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HistoryModal;
